import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wallet, Target, ShoppingCart, FileText, UserPlus, Loader2, Building2, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const Tile = ({ icon: Icon, label, sub, onClick, testid, bg }) => (
  <motion.button type="button" onClick={onClick} whileTap={{ scale: 0.94 }} data-testid={testid}
    className="relative w-full rounded-3xl p-4 text-white shadow-xl overflow-hidden flex flex-col items-center justify-center gap-2 min-h-[120px] border border-white/10"
    style={{ background: bg }}>
    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center"><Icon className="w-8 h-8" strokeWidth={2.4} /></div>
    <p className="font-black text-base">{label}</p>
    {sub && <span className="text-[10px] font-bold bg-white/20 rounded-full px-2 py-0.5">{sub}</span>}
  </motion.button>
);

const EarningsCard = ({ stats, onWithdraw, busy }) => (
  <div className="rounded-3xl p-5 text-white shadow-2xl relative overflow-hidden"
    style={{ background: "linear-gradient(135deg,#16A34A 0%,#15803D 55%,#065F46 100%)" }} data-testid="mandoob-earnings">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center"><Wallet className="w-7 h-7" /></div>
      <div className="flex-1">
        <p className="text-xs opacity-90 font-bold">أرباح المندوب — الدورة الحالية</p>
        <p className="font-black text-3xl" dir="ltr" data-testid="mandoob-earned">{fmt(stats.earned)} <span className="text-sm">د.ع</span></p>
      </div>
    </div>
    <div className="mt-4 space-y-1.5">
      <div className="flex justify-between text-[11px] font-bold opacity-95">
        <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" /> التارجت: <span dir="ltr">{fmt(stats.monthly_target)}</span></span>
        <span>مبيعاتك: <span dir="ltr">{fmt(stats.month_sales)}</span></span>
      </div>
      <div className="h-3 rounded-full bg-white/20 overflow-hidden">
        <div className="h-full rounded-full bg-yellow-300 transition-all" style={{ width: `${stats.progress_pct}%` }} />
      </div>
    </div>
    <p className="mt-3 text-center font-black text-sm bg-white/15 rounded-2xl py-2" data-testid="mandoob-target-note">
      {stats.target_reached ? "🎉 حققت التارجت — استلم أرباحك" : "⭐ ملاحظة: حقق التارجت واستلم أرباحك"}
    </p>
    <button type="button" onClick={onWithdraw} disabled={busy || !stats.target_reached || stats.available <= 0}
      data-testid="mandoob-withdraw-btn"
      className="mt-3 w-full bg-white text-[#15803D] font-black rounded-2xl py-3 disabled:opacity-60 flex items-center justify-center gap-2">
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
      طلب سحب الأرباح {stats.available > 0 && <span dir="ltr">({fmt(stats.available)})</span>}
    </button>
  </div>
);

const MandoobHome = ({ name, onDirectSale, onMySales, onAddCustomer, onBrands, onCustomers }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/mandoob/me`).then(({ data }) => setData(data)).catch(() => toast.error("فشل تحميل الأرباح"));
  useEffect(() => { load(); }, []);

  const withdraw = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/mandoob/withdraw-request`);
      toast.success("تم إرسال طلب السحب للإدارة");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الطلب"); }
    finally { setBusy(false); }
  };

  return (
    <div className="-mx-3 -my-4 px-4 py-6 min-h-[calc(100vh-60px)] space-y-5" dir="rtl" data-testid="mandoob-home"
      style={{ background: "radial-gradient(circle at top, #1E3A8A 0%, #0B1E4A 55%, #050F2C 100%)" }}>
      <div className="text-center">
        <p className="text-blue-200 text-xs font-bold">أهلاً بك</p>
        <h1 className="text-white font-black text-3xl" data-testid="mandoob-name">{name}</h1>
      </div>
      {data && !data.active && (
        <div className="rounded-2xl bg-red-600 text-white text-center font-black py-3 shadow-lg" data-testid="mandoob-stopped-banner">
          ⛔ ممنوع البيع — حسابك موقوف من الإدارة
        </div>
      )}
      {data ? <EarningsCard stats={data.stats} onWithdraw={withdraw} busy={busy} /> : (
        <div className="py-10 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-white" /></div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Tile icon={ShoppingCart} label="قائمة بيع" sub={data && !data.active ? "ممنوع البيع" : "فاتورة دين"}
          onClick={data && !data.active ? () => toast.error("ممنوع البيع — حسابك موقوف") : onDirectSale} testid="mandoob-sale-icon"
          bg="linear-gradient(135deg,#2563EB,#1E40AF)" />
        <Tile icon={FileText} label="الفواتير السابقة" sub={data ? `${data.stats.invoices_count} بالدورة` : ""} onClick={onMySales}
          testid="mandoob-invoices-icon" bg="linear-gradient(135deg,#7C3AED,#4C1D95)" />
        <Tile icon={Building2} label="الشركات" sub="أرباحك وتارجتك" onClick={onBrands} testid="mandoob-brands-icon"
          bg="linear-gradient(135deg,#F59E0B,#B45309)" />
        <Tile icon={Users} label="الزبائن" sub="الديون وآخر فاتورة" onClick={onCustomers} testid="mandoob-customers-icon"
          bg="linear-gradient(135deg,#EC4899,#9D174D)" />
        <div>
          <Tile icon={UserPlus} label="إضافة زبون" onClick={data && !data.active ? () => toast.error("ممنوع — حسابك موقوف") : onAddCustomer} testid="mandoob-add-customer-icon"
            bg="linear-gradient(135deg,#0EA5E9,#0369A1)" />
        </div>
      </div>
      {data?.withdrawals?.length > 0 && (
        <div className="bg-white/10 rounded-2xl p-3 space-y-1.5" data-testid="mandoob-withdrawals">
          <p className="text-white font-black text-sm">طلبات السحب</p>
          {data.withdrawals.map((w) => (
            <div key={w.id} className="flex justify-between text-xs text-blue-100">
              <span dir="ltr">{fmt(w.amount)} د.ع</span>
              <span className="font-bold">{{ pending: "⏳ بانتظار الإدارة", approved: "✅ تم الصرف", rejected: "❌ مرفوض" }[w.status]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MandoobHome;
